import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';
import {
  keepSectionTopDuringExpand,
  scrollSectionAfterCollapse,
} from '../../../utils/scrollAfterCollapse';
import {
  HOME_SECTION_NAVIGATION_DELAY,
  HOME_SECTION_NAVIGATION_EVENT,
} from '../../../utils/homeSectionNavigation';
import './style.css';

const MOBILE_QUERY = '(max-width: 767px)';
const BORDER_FLASH_DURATION = 220;
const HomeAccordionContext = createContext(null);

const useMobileViewport = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const handleChange = event => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
};

export const HomeSectionsAccordionProvider = ({ children }) => {
  const [openSection, setOpenSection] = useState(null);
  const [closingSection, setClosingSection] = useState(null);
  const closingTimerRef = useRef(null);
  const isMobile = useMobileViewport();

  useEffect(() => {
    if (!isMobile) {
      window.clearTimeout(closingTimerRef.current);
      setOpenSection(null);
      setClosingSection(null);
    }
  }, [isMobile]);

  useEffect(
    () => () => window.clearTimeout(closingTimerRef.current),
    []
  );

  const openAccordionSection = useCallback(sectionId => {
    window.clearTimeout(closingTimerRef.current);
    setClosingSection(null);
    setOpenSection(sectionId);
  }, []);

  const closeAccordionSection = useCallback(sectionId => {
    window.clearTimeout(closingTimerRef.current);
    setClosingSection(sectionId);
    setOpenSection(null);
    closingTimerRef.current = window.setTimeout(
      () => setClosingSection(null),
      BORDER_FLASH_DURATION
    );
  }, []);

  return (
    <HomeAccordionContext.Provider
      value={{
        isMobile,
        openSection,
        closingSection,
        openAccordionSection,
        closeAccordionSection,
      }}
    >
      {children}
    </HomeAccordionContext.Provider>
  );
};

const HomeSectionAccordion = ({
  id,
  title,
  summary,
  summaryClassName = '',
  summaryContainerClassName = '',
  collapsedSummary,
  children,
}) => {
  const context = useContext(HomeAccordionContext);
  const { hash } = useLocation();
  const headingRef = useRef(null);
  const navigationTimerRef = useRef(null);

  if (!context) {
    throw new Error(
      'HomeSectionAccordion must be used inside HomeSectionsAccordionProvider'
    );
  }

  const {
    isMobile,
    openSection,
    closingSection,
    openAccordionSection,
    closeAccordionSection,
  } = context;
  const isOpen = isMobile && openSection === id;
  const isClosing = isMobile && closingSection === id;
  const panelId = `home-${id}-panel`;
  const headingId = `home-${id}-heading`;

  useEffect(() => {
    if (!isMobile || hash !== `#${id}`) return;

    openAccordionSection(id);
  }, [hash, id, isMobile, openAccordionSection]);

  useEffect(() => {
    if (!isMobile) return undefined;

    const handleSectionNavigation = event => {
      if (event.detail?.sectionId === id) {
        openAccordionSection(id);

        if (event.detail.scrollAfterOpen) {
          window.clearTimeout(navigationTimerRef.current);
          navigationTimerRef.current = window.setTimeout(() => {
            headingRef.current?.closest('section')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }, HOME_SECTION_NAVIGATION_DELAY);
        }
      }
    };

    window.addEventListener(
      HOME_SECTION_NAVIGATION_EVENT,
      handleSectionNavigation
    );

    return () => {
      window.clearTimeout(navigationTimerRef.current);
      window.removeEventListener(
        HOME_SECTION_NAVIGATION_EVENT,
        handleSectionNavigation
      );
    };
  }, [id, isMobile, openAccordionSection]);

  const handleCollapse = () => {
    const section = headingRef.current?.parentElement;
    scrollSectionAfterCollapse(section);
    closeAccordionSection(id);
  };

  const handleExpand = () => {
    const section = headingRef.current?.parentElement;
    keepSectionTopDuringExpand(section);
    openAccordionSection(id);
  };

  return (
    <>
      <h1
        className={`title-1 home-section-accordion__heading${isOpen ? ' is-open' : ''}${isClosing ? ' is-closing' : ''}`}
        id={headingId}
        ref={headingRef}
      >
        {isMobile ? (
          <button
            type="button"
            className="home-section-accordion__toggle"
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => {
              if (isOpen) {
                handleCollapse();
                return;
              }

              handleExpand();
            }}
          >
            <span className="home-section-accordion__heading-copy">
              <span className="home-section-accordion__title">{title}</span>
              {(summary || collapsedSummary) && (
                <span
                  className={`home-section-accordion__summary${collapsedSummary && !summary ? ' home-section-accordion__summary--collapsible' : ''}`}
                >
                  <span>{summary || collapsedSummary}</span>
                </span>
              )}
            </span>
            {isOpen ? (
              <LuChevronUp
                className="home-section-accordion__chevron"
                aria-hidden="true"
              />
            ) : (
              <LuChevronDown
                className="home-section-accordion__chevron"
                aria-hidden="true"
              />
            )}
          </button>
        ) : (
          title
        )}
      </h1>

      {!isMobile && summary && (
        <div className={summaryContainerClassName || undefined}>
          <p className={summaryClassName || undefined}>{summary}</p>
        </div>
      )}

      <div
        className={`home-section-accordion__panel${isOpen ? ' is-open' : ''}`}
        id={panelId}
        aria-labelledby={headingId}
        aria-hidden={isMobile ? !isOpen : undefined}
      >
        <div className="home-section-accordion__panel-inner">
          {children}
          <button
            type="button"
            className="home-section-accordion__collapse"
            aria-controls={panelId}
            onClick={handleCollapse}
          >
            <span>Свернуть</span>
            <LuChevronUp aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
};

export default HomeSectionAccordion;
